import { Dispatch, Fragment, SetStateAction, useCallback, useEffect } from 'react';
import { Avatar, Col, Divider, Flex, List, Row, Skeleton, Spin, Tooltip } from 'antd';
import { useGetProjectAllData } from 'api/all-data/use-get-project-all-data';
import { RightSectionAllDataProps, UserProjectRole } from 'api/types';
import { Checkbox } from 'components/checkbox';
import { NodePagination } from 'components/pagination';
import { SecondaryText, Text } from 'components/typography';
import { COLORS, initPageData } from 'helpers/constants';
import { AllDataResponse } from 'types/node';
import { defaultAllDataFilter } from '../right-section-all-data';
import { Button } from 'components/button';
import { useViewDatasheet } from 'context/datasheet-view-vontext';
import { useProject } from 'context/project-context';
import { useParams } from 'react-router-dom';
import { useIsXXlScreen } from 'hooks/use-breakpoint';
import { useIsPublicPage } from 'hooks/use-is-public-page';
import { ReactComponent as DefaultIconSvg } from 'components/icons/default-image.svg';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { FileTextOutlined } from '@ant-design/icons';
import { Icon } from 'components/icon';

import { Draft } from '../connection-reject-table/style';
import { MarkedText } from 'components/typography/marked-text';

type TypeInfoProps = {
  color?: string;
};

const TypeInfo = styled(({ color, ...props }) => <div {...props} />)<TypeInfoProps>`
  background-color: ${(props) => `${props.color}15`};
  text-align: center;
  border-radius: 8px;
`;

const StyledListItem = styled(({ color, ...props }) => <List.Item {...props} />)`
  && {
    margin-bottom: 8px;
    padding-left: 24px;
    padding-right: 32px;
    background: linear-gradient(90.12deg, rgba(255, 255, 255, 0.7) 1.63%, rgba(255, 255, 255, 0.2) 100%);

    &:hover {
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);

      .all-data-type-name {
        background-color: ${(props) => `${props.color}`};
      }
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .default-name-btn {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      max-width: 35vw;

      .default-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
`;

type Props = {
  filterValue: typeof defaultAllDataFilter;
  checkedItems: RightSectionAllDataProps[];
  setCheckedItems: (values: RightSectionAllDataProps[]) => void;
  setIsAllCheck: Dispatch<SetStateAction<boolean>>;
  isAllCheck: boolean;
  setFilterValue: (
    filter: typeof defaultAllDataFilter | ((prevVar: typeof defaultAllDataFilter) => typeof defaultAllDataFilter)
  ) => void;
};

export const AllDataListNode = ({
  filterValue,
  setFilterValue,
  checkedItems,
  setCheckedItems,
  isAllCheck,
  setIsAllCheck,
}: Props) => {
  const params = useParams();
  const { dispatch } = useViewDatasheet();
  const { projectInfo } = useProject();
  const isXXl = useIsXXlScreen();
  const isPublicPage = useIsPublicPage();

  const {
    rowsData,
    count: pageCount,
    isInitialLoading,
  } = useGetProjectAllData(filterValue, {
    enabled: !!(filterValue.project_type_list_id && filterValue.type === 'node'),
    onSuccess: (data) => {
      if (params.node_type_id) {
        dispatch(params.node_type_id);
      }
    },
  });

  const handleItemClick = useCallback(
    (item: AllDataResponse) => {
      const updatedCheckedItems = checkedItems.some((checkedItem) => checkedItem.id === item.id)
        ? checkedItems.filter((checkedItem) => checkedItem.id !== item.id)
        : [...checkedItems, { id: item.id, checked: false, status: item.status }];

      setIsAllCheck(updatedCheckedItems.length === rowsData.length);
      setCheckedItems(updatedCheckedItems);
    },
    [checkedItems, rowsData.length, setCheckedItems, setIsAllCheck]
  );

  useEffect(() => {
    if (isAllCheck) {
      setCheckedItems(rowsData.map((item: AllDataResponse) => ({ id: item.id, checked: true, status: item.status })));
    }
  }, [isAllCheck, rowsData, setCheckedItems]);

  return (
    <Spin spinning={isInitialLoading}>
      {projectInfo?.role !== UserProjectRole.Viewer && !isPublicPage && (
        <div style={{ margin: '1rem 1.5rem', position: 'absolute', top: -60 }}>
          <Checkbox
            id="allCheck"
            className="all-data-checkbox"
            style={{ marginRight: '16px' }}
            checked={isAllCheck}
            onChange={() => {
              if (!isAllCheck) {
                setCheckedItems(
                  rowsData.map((item: AllDataResponse) => ({
                    id: item.id,
                    checked: true,
                    status: item.status,
                  }))
                );
              } else {
                setCheckedItems([]);
              }
              setIsAllCheck((prev) => !prev);
            }}
          />
          <label
            htmlFor="allCheck"
            style={{
              cursor: 'pointer',
              color: '#808080',
              fontWeight: '600',
              letterSpacing: '1.4px',
            }}
          >
            All Nodes
          </label>
        </div>
      )}
      <List
        style={{ overflow: 'auto', height: `calc(100vh - ${(isXXl ? 152 : 152) + 208}px)` }}
        pagination={false}
        dataSource={rowsData}
        renderItem={(item, index) => {
          return (
            <StyledListItem key={item.id} color={item.nodeType?.color}>
              {projectInfo?.role !== UserProjectRole.Viewer && !isPublicPage && (
                <Checkbox
                  className="all-data-checkbox"
                  style={{ marginRight: 16 }}
                  checked={isAllCheck || checkedItems.some((checkedItem) => checkedItem.id === item.id)}
                  onClick={() => handleItemClick(item)}
                />
              )}
              <List.Item.Meta
                avatar={
                  item.default_image ? (
                    <Avatar src={`${process.env.REACT_APP_AWS_URL}${item.default_image}`} />
                  ) : (
                    <DefaultIconSvg />
                  )
                }
                title={
                  <Row align="middle">
                    <Col span={6}>
                      <Button
                        className="default-name-btn"
                        onClick={() => dispatch(item.id ?? item?.node_id)}
                        type="link"
                      >
                        <Tooltip title={item.name} placement="bottom">
                          <Text className="default-name" color={COLORS.PRIMARY.GRAY}>
                            {item.name ? item?.name : item?.node_name}
                          </Text>
                        </Tooltip>
                      </Button>
                    </Col>
                    <Col span={18}>
                      <Row justify="end" align="middle" gutter={32}>
                        {!item?.type_name && (
                          <Col span={6}>
                            {item?.documents !== undefined && item.documents > 0 && (
                              <Row gutter={8} align="middle">
                                <Col>
                                  <span style={{ color: COLORS.BLUE_10 }}>{item?.documents}</span>
                                </Col>
                                <Col>
                                  <FileTextOutlined style={{ color: COLORS.BLUE_10 }} />
                                </Col>
                              </Row>
                            )}
                          </Col>
                        )}
                        {item.status === 'draft' && <Draft>draft</Draft>}
                        {item?.type_name && (
                          <Col xs={18} xxl={14}>
                            {item.data?.map((dataItem, index) => (
                              <Fragment key={index}>
                                <Row justify="end" align="top" gutter={32}>
                                  <Col xs={10} xxl={9}>
                                    <Button
                                      block
                                      type="link"
                                      onClick={() => {
                                        window.open(dataItem.path, '_blank');
                                      }}
                                      icon={<Icon color="red" icon="file1" size={16} />}
                                    >
                                      <SecondaryText ellipsis>
                                        <MarkedText longText={dataItem.match_filename || dataItem.filename} />
                                      </SecondaryText>
                                    </Button>
                                  </Col>
                                  <Col xs={11} xxl={12}>
                                    <MarkedText longText={dataItem.match_content} />
                                  </Col>
                                  <Col xs={3} xxl={2}>
                                    <Text color={COLORS.PRIMARY.GRAY}>({dataItem.match_count})</Text>
                                  </Col>
                                </Row>
                                <Divider />
                              </Fragment>
                            ))}
                          </Col>
                        )}
                        <Col span={6}>
                          <TypeInfo color={item.nodeType?.color ?? item?.color} className="all-data-type-name">
                            <Text color={COLORS.PRIMARY.GRAY_DARK}>
                              {item.nodeType?.name} {item?.type_name}
                            </Text>
                          </TypeInfo>
                        </Col>
                        {!item?.type_name && (
                          <Col span={6}>
                            <Flex vertical gap={8}>
                              <Text color={COLORS.PRIMARY.GRAY}>
                                {item?.updated_at ? dayjs(item?.updated_at).format('DD MMMM [at] HH:mm') : ''}
                              </Text>
                              {item?.created ? (
                                <Text color={COLORS.PRIMARY.BLUE}>
                                  By {item?.created.first_name} {item?.created.last_name}
                                </Text>
                              ) : (
                                ''
                              )}
                            </Flex>
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                }
              />
            </StyledListItem>
          );
        }}
      />
      {pageCount ? (
        <NodePagination
          total={pageCount}
          defaultPageSize={initPageData.size}
          pageSize={filterValue.size}
          defaultCurrent={initPageData.page}
          current={filterValue.page}
          onChange={(page) => {
            setFilterValue((prev) => ({ ...prev, page, allowDocumentCount: false }));
          }}
          onShowSizeChange={(current, size) => {
            setFilterValue((prev) => ({ ...prev, page: current, size: size, allowDocumentCount: false }));
          }}
          style={{ margin: '10px 30px', padding: 0 }}
        />
      ) : (
        isInitialLoading && <Skeleton />
      )}
    </Spin>
  );
};
